import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { BarChart3, CheckCircle2, Flag, RotateCcw, Trash2, Wifi, WifiOff } from "lucide-react";
import { useT } from "@/lib/i18n";
import { computeStats, matchResult, TEAM_NAME } from "@/lib/dvStats";
import { adminClient } from "@/lib/api";
import DesktopGrid from "./DesktopGrid";
import MobileBoard from "./MobileBoard";
import EndSetDialog from "./EndSetDialog";
import SetTabs from "./SetTabs";
import { useMatchEvents } from "./useMatchEvents";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const setKey = (m) => `dv_set_${m}`;

export default function LiveBoard({ match, onViewStats, onMatchUpdate }) {
  const { t } = useT();
  const { events, record, undo, resetSet, pending, online, canUndo } = useMatchEvents(match.id);
  const [set, setSet] = useState(() => {
    const s = localStorage.getItem(setKey(match.id));
    return s === "total" ? "total" : Number(s) || 1;
  });
  const [flash, setFlash] = useState(null);
  const [flashRow, setFlashRow] = useState(null);
  const [endSetOpen, setEndSetOpen] = useState(false);
  const [finishOpen, setFinishOpen] = useState(false);
  const result = matchResult(match.set_scores);
  const locked = set !== "total" && result.decided && set > result.decidedAt;

  useEffect(() => localStorage.setItem(setKey(match.id), String(set)), [set, match.id]);

  const readOnly = set === "total" || locked;
  const stats = useMemo(
    () => computeStats(match.roster, events, readOnly ? null : set),
    [match.roster, events, set, readOnly]
  );

  const bump = (pid, category, type, delta) => {
    if (readOnly) return;
    const key = `${category}_${type}`;
    if (delta < 0 && (stats.players[pid]?.[key] ?? 0) <= 0) return;
    record({ set_number: set, player_id: pid, category, type, delta });
    setFlash(`${pid}_${key}`);
    setFlashRow(pid);
    setTimeout(() => setFlash(null), 320);
    setTimeout(() => setFlashRow(null), 500);
  };

  const handleUndo = () => {
    const last = undo();
    if (last) {
      setFlashRow(last.player_id);
      setTimeout(() => setFlashRow(null), 500);
    }
  };

  const finishMatch = async () => {
    try {
      const { data } = await adminClient.patch(`/datavolley/matches/${match.id}`, { status: "finished" });
      onMatchUpdate?.(data);
      setFinishOpen(false);
      toast.success(t("dv_finished"));
    } catch {
      toast.error("Error");
    }
  };

  const handleReset = async () => {
    try {
      await resetSet(set);
      toast.success(`SET ${set} — ${t("dv_reset_set")}`);
    } catch {
      toast.error(online ? "Error" : t("dv_offline"));
    }
  };

  return (
    <div className="flex flex-col gap-2 lg:gap-3" data-testid="dv-live-board">
      <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-3 rounded-2xl bg-slate-900 px-3 sm:px-4 py-2.5 sm:py-3 text-white">
        <div className="flex items-center gap-3 min-w-0">
          <div className="min-w-0">
            <p className="font-heading font-black text-base sm:text-xl text-white leading-none" data-testid="dv-live-team">
              {TEAM_NAME} <span className="text-slate-400 text-sm font-bold">vs</span>
            </p>
            <p className="font-heading font-black text-base sm:text-xl truncate" data-testid="dv-live-opponent">
              {match.opponent}
            </p>
          </div>
        </div>
        <div className="w-full sm:w-auto overflow-x-auto order-3 sm:order-none -mx-1 px-1">
          <div className="hidden sm:block"><SetTabs value={set} onChange={setSet} size="lg" scores={match.set_scores} /></div>
          <div className="sm:hidden"><SetTabs value={set} onChange={setSet} size="md" scores={match.set_scores} /></div>
        </div>
        <div
          data-testid="dv-sync-status"
          className={`flex items-center gap-1.5 text-[11px] sm:text-xs font-semibold px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full ${
            !online ? "bg-amber-500/20 text-amber-300" : pending > 0 ? "bg-sky-500/20 text-sky-300" : "bg-emerald-500/15 text-emerald-300"
          }`}
        >
          {online ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
          {!online ? t("dv_offline") : pending > 0 ? `${pending} ${t("dv_pending")}` : t("dv_synced")}
        </div>
      </div>

      {result.decided && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl bg-emerald-50 border border-emerald-200 px-4 py-2.5" data-testid="dv-decided-banner">
          <p className="text-sm font-bold text-emerald-800 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" /> {t("dv_match_decided")}: {TEAM_NAME} {result.us} - {result.them} {match.opponent}
            {locked && <span className="font-medium text-emerald-700">· {t("dv_set_locked")}</span>}
          </p>
          {match.status !== "finished" && (
            <button type="button" onClick={finishMatch} data-testid="dv-finish-match-btn" className="dv-btn h-9 px-4 rounded-lg bg-emerald-600 text-white text-sm font-bold">
              {t("dv_finish_match")}
            </button>
          )}
        </div>
      )}
      <DesktopGrid match={match} stats={stats} flash={flash} flashRow={flashRow} readOnly={readOnly} bump={bump} t={t} />
      <MobileBoard match={match} stats={stats} flash={flash} readOnly={readOnly} bump={bump} t={t} />

      <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-10 gap-2 lg:gap-3">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              type="button"
              disabled={readOnly}
              data-testid="dv-reset-set-btn"
              className="dv-btn h-12 lg:h-16 rounded-xl lg:rounded-2xl bg-red-600 text-white text-sm lg:text-base font-bold flex items-center justify-center gap-2 disabled:opacity-40"
            >
              <Trash2 className="h-5 w-5" /> {t("dv_reset_set")}
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>SET {set} — {t("dv_reset_set")}</AlertDialogTitle>
              <AlertDialogDescription>{t("dv_reset_confirm")}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="dv-reset-cancel">{t("dv_cancel")}</AlertDialogCancel>
              <AlertDialogAction data-testid="dv-reset-confirm" onClick={handleReset} className="bg-red-600 hover:bg-red-700">
                {t("dv_reset_set")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <TotalCard label={t("dv_total_rec")} value={stats.team.receptionTotal} testid="dv-total-rec" />
        <TotalCard label={t("dv_total_att")} value={stats.team.attackTotal} testid="dv-total-att" />
        <TotalCard label={t("dv_total_srv")} value={stats.team.serveTotal} testid="dv-total-srv" />
        <TotalCard label={t("dv_total_blk")} value={stats.team.blocks} testid="dv-total-blk" />
        <TotalCard label={t("dv_set_pct")} value={`${stats.team.setPct}%`} testid="dv-total-setpct" accent="text-emerald-700" />
        <TotalCard label={t("dv_errors")} value={stats.team.errorsTotal} testid="dv-total-errors" accent="text-slate-700" />

        <button
          type="button"
          onClick={() => setEndSetOpen(true)}
          disabled={readOnly}
          data-testid="dv-end-set-btn"
          className="dv-btn h-12 lg:h-16 rounded-xl lg:rounded-2xl bg-sunset text-white text-sm lg:text-base font-bold flex items-center justify-center gap-2 disabled:opacity-40"
        >
          <Flag className="h-5 w-5" />
          <span className="text-left leading-tight">
            <span className="block">{t("dv_end_set")}</span>
            {!readOnly && match.set_scores?.[String(set)] && (
              <span className="block text-[11px] font-semibold opacity-90">{match.set_scores[String(set)][0]} - {match.set_scores[String(set)][1]}</span>
            )}
          </span>
        </button>
        <button
          type="button"
          onClick={handleUndo}
          disabled={!canUndo}
          data-testid="dv-undo-btn"
          className="dv-btn h-12 lg:h-16 rounded-xl lg:rounded-2xl bg-white border-2 border-slate-300 text-slate-900 flex items-center justify-center gap-2 lg:gap-3 disabled:opacity-40"
        >
          <RotateCcw className="h-5 w-5" />
          <span className="text-left leading-tight">
            <span className="block font-bold text-sm lg:text-base">{t("dv_undo")}</span>
            <span className="hidden lg:block text-[11px] text-slate-500">{t("dv_undo_sub")}</span>
          </span>
        </button>
        <button
          type="button"
          onClick={onViewStats}
          data-testid="dv-view-stats-btn"
          className="dv-btn h-12 lg:h-16 rounded-xl lg:rounded-2xl bg-emerald-600 text-white text-sm lg:text-base font-bold flex items-center justify-center gap-2"
        >
          <BarChart3 className="h-5 w-5" /> {t("dv_view_stats")}
        </button>
      </div>
      {!readOnly && (
        <EndSetDialog
          open={endSetOpen}
          onOpenChange={setEndSetOpen}
          match={match}
          setNumber={set}
          onSaved={(m) => {
            onMatchUpdate?.(m);
            const r = matchResult(m.set_scores);
            if (r.decided) {
              if (m.status !== "finished") setFinishOpen(true);
            } else if (set < 5) {
              setSet(set + 1);
            }
          }}
        />
      )}
      <AlertDialog open={finishOpen} onOpenChange={setFinishOpen}>
        <AlertDialogContent data-testid="dv-finish-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("dv_match_decided")} · {result.us} - {result.them}</AlertDialogTitle>
            <AlertDialogDescription>{t("dv_finish_match_q")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="dv-finish-cancel">{t("dv_not_now")}</AlertDialogCancel>
            <AlertDialogAction data-testid="dv-finish-confirm" onClick={finishMatch} className="bg-emerald-600 hover:bg-emerald-700">
              {t("dv_finish_match")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function TotalCard({ label, value, testid, accent = "text-slate-900" }) {
  return (
    <div className="h-12 lg:h-16 rounded-xl lg:rounded-2xl bg-white border border-slate-200 flex flex-col items-center justify-center" data-testid={testid}>
      <span className="text-[10px] lg:text-[11px] font-semibold text-slate-500 leading-none">{label}</span>
      <span className={`font-heading font-black text-lg lg:text-2xl ${accent} leading-none mt-0.5`}>{value}</span>
    </div>
  );
}
